/**
 * La imagen de una pregunta, en el editor (§9.3).
 *
 * **La conversión ocurre aquí, en el navegador de quien sube.** El móvil que hace la foto es el que
 * la reduce: lo que viaja por la wifi del centro son 200 kB en lugar de ocho megas, y el servidor
 * no gasta ni CPU ni ancho de banda en algo que el cliente ya puede hacer.
 *
 * El error se dice con palabras, siempre. Una subida que falla en silencio es la peor variante: el
 * profesor cree que la imagen está y descubre que no cuando la clase mira al proyector.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvisos } from './sistema/avisos.js';
import { removeQuestionImage, uploadQuestionImage, usedImageBytes } from '../images/api.js';
import { DescripcionImagen, ImagenPregunta } from './imagen-pregunta.js';

export interface ImagenGuardada {
  readonly path: string | null;
  readonly alt: string | null;
  readonly width: number | null;
  readonly height: number | null;
}

export function ImagenEditor({
  quizId,
  value,
  onChange,
}: {
  quizId: string;
  value: ImagenGuardada;
  onChange: (image: ImagenGuardada) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const [subiendo, setSubiendo] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const usados = useRef(0);

  useEffect(() => {
    let vigente = true;
    void usedImageBytes().then((bytes) => {
      if (vigente) usados.current = bytes;
    });
    return () => {
      vigente = false;
    };
  }, []);

  const elegir = async (file: File): Promise<void> => {
    setSubiendo(true);

    const resultado = await uploadQuestionImage(file, quizId, usados.current);

    if (!resultado.ok) {
      // Un suceso, no un estado: se cuenta al pie y se va. Como bloque bajo el control, empujaba
      // el resto de la pregunta hacia abajo y se quedaba ahí hasta el siguiente intento.
      avisos.fallo(t(resultado.reason));
      setSubiendo(false);
      return;
    }

    // La anterior se borra DESPUÉS de que la nueva esté arriba: al revés, un fallo de subida
    // dejaría la pregunta sin imagen y sin forma de recuperarla.
    const anterior = value.path;
    onChange({
      path: resultado.image.path,
      alt: value.alt,
      width: resultado.image.width,
      height: resultado.image.height,
    });
    usados.current += resultado.image.bytes;
    if (anterior !== null) await removeQuestionImage(anterior);

    setSubiendo(false);
  };

  const quitar = async (): Promise<void> => {
    const anterior = value.path;
    onChange({ path: null, alt: null, width: null, height: null });
    if (anterior !== null) await removeQuestionImage(anterior);
  };

  return (
    <div className="border-line rounded-card mt-4 border-2 p-3">
      <p className="text-sm font-semibold">{t('images.label')}</p>

      {value.path === null ? null : (
        <div className="mt-3">
          <ImagenPregunta
            path={value.path}
            alt={value.alt}
            width={value.width}
            height={value.height}
            className="max-h-40 w-auto rounded-lg object-contain"
          />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {/*
         * El campo va oculto y lo dispara el botón, pero **lleva etiqueta igual**: sin ella, un
         * lector de pantalla lo anuncia como «campo sin nombre» si llega hasta él. Y sale del
         * orden de tabulación, porque el botón de al lado ya cumple esa función y tabular dos
         * veces al mismo sitio confunde a quien navega con teclado.
         */}
        <input
          ref={input}
          type="file"
          aria-label={t('images.add')}
          tabIndex={-1}
          // HEIC incluido: es lo que sale de un iPhone, y rechazarlo obligaría a convertir a mano
          // justo a quien más fotos va a subir.
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void elegir(file);
            // Se limpia para que elegir el mismo archivo dos veces vuelva a disparar el evento.
            event.target.value = '';
          }}
        />
        <button
          type="button"
          disabled={subiendo}
          onClick={() => input.current?.click()}
          className="border-line rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {subiendo
            ? t('images.uploading')
            : value.path === null
              ? t('images.add')
              : t('images.replace')}
        </button>

        {value.path === null ? null : (
          <button
            type="button"
            onClick={() => void quitar()}
            className="border-ko/60 text-ko rounded-lg border px-3 py-1.5 text-sm"
          >
            {t('images.remove')}
          </button>
        )}
      </div>

      {value.path === null ? null : (
        <DescripcionImagen
          value={value.alt ?? ''}
          onChange={(alt) => onChange({ ...value, alt: alt === '' ? null : alt })}
        />
      )}
    </div>
  );
}
